import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Plus, Globe, CheckCircle2, AlertCircle, Loader2, ExternalLink, Link2 } from 'lucide-react';
import { domainApi, gscApi, type Domain } from '@/lib/api';
import { formatNumber, cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';

export function DashboardHome() {
  const { user, usage, pool } = useAuth();

  const { data: domainsData, isLoading: domainsLoading } = useQuery({
    queryKey: ['domains'],
    queryFn: domainApi.list,
  });

  const { data: gscStatus } = useQuery({
    queryKey: ['gsc-status'],
    queryFn: gscApi.status,
  });

  const domains = domainsData?.domains || [];
  const isGscConnected = gscStatus?.connected;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold">Dashboard</h1>
          {user && (
            <p className="text-slate-600">
              Welcome back{user.name ? `, ${user.name}` : ''}!
            </p>
          )}
        </div>
        <Link
          to="/domains/new"
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90"
        >
          <Plus className="w-4 h-4" />
          Add Domain
        </Link>
      </div>

      {/* URL Pool Overview */}
      {pool && (
        <div className="bg-gradient-to-r from-primary/10 to-blue-50 rounded-xl border p-6 mb-8">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="font-semibold text-lg">URL Pool</h2>
              <p className="text-sm text-slate-600">Allocate URLs across your domains</p>
            </div>
            <span className="px-3 py-1 bg-white rounded-full text-sm font-medium capitalize">{user?.plan}</span>
          </div>
          <div className="grid grid-cols-3 gap-6">
            <div>
              <p className="text-sm text-slate-500">Total Pool</p>
              <p className="text-2xl font-bold">
                {pool.url_pool === -1 ? 'Unlimited' : formatNumber(pool.url_pool)}
              </p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Allocated</p>
              <p className="text-2xl font-bold">{formatNumber(pool.allocated)}</p>
            </div>
            <div>
              <p className="text-sm text-slate-500">Available</p>
              <p className="text-2xl font-bold text-green-600">
                {pool.available === -1 ? 'Unlimited' : formatNumber(pool.available)}
              </p>
            </div>
          </div>
          {pool.url_pool !== -1 && (
            <div className="mt-4">
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-500">Pool allocation</span>
                <span className="font-medium">{Math.round((pool.allocated / pool.url_pool) * 100)}%</span>
              </div>
              <div className="h-3 bg-white rounded-full overflow-hidden">
                <div
                  className="h-full bg-primary rounded-full transition-all"
                  style={{ width: `${Math.min((pool.allocated / pool.url_pool) * 100, 100)}%` }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Usage Stats */}
      {usage && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <UsageCard
            label="Domains"
            used={usage.domainsUsed}
            limit={usage.domainsLimit}
          />
          <UsageCard
            label="URLs Used"
            used={pool?.used || usage.urlsUsed}
            limit={pool?.url_pool || usage.urlsLimit}
          />
          <div className="bg-white rounded-xl border p-4">
            <p className="text-sm text-slate-500 mb-1">Current Plan</p>
            <p className="text-2xl font-bold capitalize">{user?.plan || 'Free'}</p>
            <Link to="/settings" className="text-sm text-primary hover:underline">
              Upgrade Plan
            </Link>
          </div>
        </div>
      )}

      {/* GSC Connection Status */}
      <div className={cn(
        'rounded-xl border p-4 mb-8 flex items-center justify-between',
        isGscConnected ? 'bg-blue-50 border-blue-200' : 'bg-slate-50 border-slate-200'
      )}>
        <div className="flex items-center gap-3">
          <div className={cn(
            'w-10 h-10 rounded-lg flex items-center justify-center',
            isGscConnected ? 'bg-blue-100' : 'bg-slate-200'
          )}>
            <Link2 className={cn('w-5 h-5', isGscConnected ? 'text-blue-600' : 'text-slate-500')} />
          </div>
          <div>
            <p className="font-medium">
              {isGscConnected ? 'Google Search Console Connected' : 'Google Search Console'}
            </p>
            <p className="text-sm text-slate-600">
              {isGscConnected
                ? 'Sitemaps are automatically submitted when generated'
                : 'Connect to auto-submit sitemaps to Google'}
            </p>
          </div>
        </div>
        {isGscConnected ? (
          <span className="flex items-center gap-1 px-3 py-1.5 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
            <CheckCircle2 className="w-4 h-4" />
            Connected
          </span>
        ) : (
          <Link
            to="/settings"
            className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90"
          >
            Connect GSC
          </Link>
        )}
      </div>

      {/* Domains List */}
      <div className="bg-white rounded-xl border shadow-sm">
        <div className="p-4 border-b">
          <h2 className="font-semibold">Your Domains</h2>
        </div>

        {domainsLoading ? (
          <div className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
            <p className="text-slate-500 mt-2">Loading domains...</p>
          </div>
        ) : domains.length === 0 ? (
          <div className="p-8 text-center">
            <Globe className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-600 font-medium">No domains yet</p>
            <p className="text-slate-500 text-sm mb-4">
              Add your first domain to get started with sitemap hosting
            </p>
            <Link
              to="/domains/new"
              className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary/90"
            >
              <Plus className="w-4 h-4" />
              Add Your First Domain
            </Link>
          </div>
        ) : (
          <div className="divide-y">
            {domains.map((domain) => (
              <DomainRow key={domain.id} domain={domain} />
            ))}
          </div>
        )}
      </div>

      {/* Quick Start Guide */}
      <div className="mt-8 bg-gradient-to-r from-primary/5 to-blue-50 rounded-xl border p-6">
        <h3 className="font-semibold mb-3">Quick Start Guide</h3>
        <ol className="space-y-3 text-sm">
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-medium">1</span>
            <div>
              <p className="font-medium">Add your domain</p>
              <p className="text-slate-600">Click "Add Domain" and enter your subdomain (e.g., sitemap.yoursite.com)</p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-medium">2</span>
            <div>
              <p className="font-medium">Configure DNS</p>
              <p className="text-slate-600">Add the CNAME record to your DNS provider</p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-medium">3</span>
            <div>
              <p className="font-medium">Upload your URLs</p>
              <p className="text-slate-600">Upload a JSON/CSV file or paste URLs directly</p>
            </div>
          </li>
          <li className="flex gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-primary text-white rounded-full flex items-center justify-center text-xs font-medium">4</span>
            <div>
              <p className="font-medium">Submit to Google</p>
              <p className="text-slate-600">We automatically submit your sitemap via GSC API</p>
            </div>
          </li>
        </ol>
      </div>
    </div>
  );
}

function UsageCard({ label, used, limit }: { label: string; used: number; limit: number }) {
  const percentage = limit === -1 ? 0 : Math.round((used / limit) * 100);
  const isUnlimited = limit === -1;

  return (
    <div className="bg-white rounded-xl border p-4">
      <p className="text-sm text-slate-500 mb-1">{label}</p>
      <p className="text-2xl font-bold">
        {formatNumber(used)}
        <span className="text-base font-normal text-slate-400">
          {isUnlimited ? ' / Unlimited' : ` / ${formatNumber(limit)}`}
        </span>
      </p>
      {!isUnlimited && (
        <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
          <div
            className={cn(
              'h-full rounded-full transition-all',
              percentage > 90 ? 'bg-red-500' : percentage > 70 ? 'bg-amber-500' : 'bg-primary'
            )}
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
      )}
    </div>
  );
}

function DomainRow({ domain }: { domain: Domain }) {
  const budgetUsed = domain.urlBudget > 0 ? Math.round((domain.urlsUsed / domain.urlBudget) * 100) : 0;

  return (
    <Link
      to={`/domains/${domain.id}`}
      className="flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors"
    >
      <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center">
        <Globe className="w-5 h-5 text-slate-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{domain.domain}</p>
        <div className="flex items-center gap-3 text-sm text-slate-500">
          <span>{formatNumber(domain.urlsUsed)} / {formatNumber(domain.urlBudget)} URLs</span>
          {domain.urlBudget > 0 && (
            <div className="flex-1 max-w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
              <div
                className={cn(
                  'h-full rounded-full',
                  budgetUsed > 90 ? 'bg-red-500' : budgetUsed > 70 ? 'bg-amber-500' : 'bg-primary'
                )}
                style={{ width: `${Math.min(budgetUsed, 100)}%` }}
              />
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {domain.verified ? (
          <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <CheckCircle2 className="w-3 h-3" />
            Verified
          </span>
        ) : (
          <span className="flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
            <AlertCircle className="w-3 h-3" />
            Pending
          </span>
        )}
        <ExternalLink className="w-4 h-4 text-slate-400" />
      </div>
    </Link>
  );
}
